import { Box } from '../joy/Box';
import { Stack } from '../joy/Stack';
import { FormControl } from '../joy/FormControl';
import { FormLabel } from '../joy/FormLabel';
import { Select } from '../joy/Select';
import { Option } from '../joy/Option';
import { Typography } from '../joy/Typography';
import { IconButton } from '../joy/IconButton';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Trans } from '@lingui/react';
import { UsePaginatedQueryResult } from './UsePaginatedQuery';

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number }) {
  return `${from}–${to} / ${count !== -1 ? count : `> ${to}`}`;
}

const getLabelDisplayedRowsTo = (totalRows: number, rowsPerPage: number, page: number) => {
  if (totalRows === -1) {
    return (page + 1) * rowsPerPage;
  }
  return rowsPerPage === -1 ? totalRows : Math.min(totalRows, (page + 1) * rowsPerPage);
};

export const TablePagination = ({
  totalRows,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
}: {
  totalRows: number;
} & Pick<UsePaginatedQueryResult, 'page' | 'rowsPerPage' | 'setPage' | 'setRowsPerPage'>) => {
  return (
    <Stack direction="row" gap={2} alignItems="center" justifyContent="end">
      <FormControl orientation="horizontal" size="sm">
        <FormLabel>
          <Trans id="Rows per page" />
        </FormLabel>
        <Select
          onChange={(_, v) => {
            if (v) {
              setRowsPerPage(v);
            }
          }}
          value={rowsPerPage}
        >
          <Option value={10}>10</Option>
          <Option value={25}>25</Option>
          <Option value={50}>50</Option>
          <Option value={100}>100</Option>
        </Select>
      </FormControl>
      <Typography sx={{ textAlign: 'center', minWidth: 80 }}>
        {labelDisplayedRows({
          from: totalRows === 0 ? 0 : page * rowsPerPage + 1,
          to: getLabelDisplayedRowsTo(totalRows, rowsPerPage, page),
          count: totalRows === -1 ? -1 : totalRows,
        })}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton
          size="sm"
          color="neutral"
          variant="outlined"
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
          sx={{ bgcolor: 'background.surface' }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <IconButton
          size="sm"
          color="neutral"
          variant="outlined"
          disabled={totalRows !== -1 ? page >= Math.ceil(totalRows / rowsPerPage) - 1 : false}
          onClick={() => setPage(page + 1)}
          sx={{ bgcolor: 'background.surface' }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>
    </Stack>
  );
};
